/**
 * @zendir/ui - MonitoringIcon Component (Pure React)
 *
 * Status monitoring icon following Astro UX Design System patterns.
 * Displays a monitoring icon with status, label, and sub-label.
 *
 * Astro UX Compliance (https://www.astrouxds.com/components/icons-and-symbols/):
 * - Must include a label and status indicator
 * - Light theme requires border around status indicator
 * - Uses 6-level status color system
 *
 * Features:
 * - Status-based coloring with theme integration
 * - Notification badge
 * - Astro icon library support
 * - Light theme border support
 * - Tooltip support
 * - Keyboard accessible
 */

import React, { memo, useState } from "react";
import { useTheme } from "../theme";
import { classNames, type StatusLevel } from "../utils";
import { Icon, isValidIconName, type IconName } from "../core/display/Icon";

export type MonitoringStatus = StatusLevel;

export interface MonitoringIconProps {
  /** Current status of the monitored item */
  status: MonitoringStatus;
  /** Primary label (always visible) */
  label: string;
  /** Sub-label for dynamic info (optional) */
  sublabel?: string;
  /** Icon name or custom icon element */
  icon?: string | React.ReactNode;
  /** Notification count badge */
  notifications?: number;
  /** Show tooltip on hover */
  tooltip?: string;
  /** Custom className */
  className?: string;
  /** Click handler */
  onClick?: () => void;

  // === Percentage Arc Display (Astro UX) ===
  /**
   * Show percentage arc display instead of icon
   * When true, displays a circular arc showing the percentage value
   */
  showPercentage?: boolean;
  /** Percentage value (0-100) - displayed as arc when showPercentage is true */
  percentage?: number;
  /** Map percentage to status colors (e.g., low = critical, high = normal) */
  percentageToStatus?: "ascending" | "descending";
  /** Show percentage value as text inside the arc */
  showPercentageValue?: boolean;
}

/**
 * Astro UX Status Colors (Light Theme - Border)
 * Required for light backgrounds per Astro UX
 */
const STATUS_BORDER_COLORS: Record<StatusLevel, string> = {
  off: "#3c3e42",
  standby: "#285766",
  normal: "#005a00",
  caution: "#645600",
  serious: "#664618",
  critical: "#661102",
};

/**
 * Render Astro-compliant status shape for MonitoringIcon
 * Shapes per https://www.astrouxds.com/patterns/status-system/
 * - Off: Circle (grey)
 * - Standby: Ring (cyan)
 * - Normal: Circle (green)
 * - Caution: Square (yellow)
 * - Serious: Diamond (orange)
 * - Critical: Triangle pointing down (red)
 */
const renderStatusSymbol = (
  status: StatusLevel,
  size: number,
  fillColor: string,
  borderColor?: string,
  glow?: boolean,
): React.ReactNode => {
  const hasBorder = !!borderColor;
  const strokeWidth = hasBorder ? 1 : 0;
  const glowFilter = glow ? `drop-shadow(0 0 3px ${fillColor})` : undefined;

  switch (status) {
    // Small filled circle: off
    case "off":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={{ filter: glowFilter }}
        >
          <circle
            cx="6"
            cy="6"
            r={hasBorder ? 2.5 : 3}
            fill={fillColor}
            stroke={borderColor}
            strokeWidth={strokeWidth}
          />
        </svg>
      );

    // Ring (donut): standby
    case "standby":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={{ filter: glowFilter }}
        >
          <circle
            cx="6"
            cy="6"
            r="3.5"
            fill="none"
            stroke={fillColor}
            strokeWidth="2"
          />
        </svg>
      );

    // Filled circle: normal
    case "normal":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={{ filter: glowFilter }}
        >
          <circle
            cx="6"
            cy="6"
            r={hasBorder ? 4.5 : 5}
            fill={fillColor}
            stroke={borderColor}
            strokeWidth={strokeWidth}
          />
        </svg>
      );

    // Square shape: caution
    case "caution":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={{ filter: glowFilter }}
        >
          <rect
            x={hasBorder ? 1.5 : 1}
            y={hasBorder ? 1.5 : 1}
            width={hasBorder ? 9 : 10}
            height={hasBorder ? 9 : 10}
            fill={fillColor}
            stroke={borderColor}
            strokeWidth={strokeWidth}
          />
        </svg>
      );

    // Diamond shape: serious (rotated square)
    case "serious":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={{ filter: glowFilter }}
        >
          <polygon
            points="6,1 11,6 6,11 1,6"
            fill={fillColor}
            stroke={borderColor}
            strokeWidth={strokeWidth}
          />
        </svg>
      );

    // Triangle pointing down: critical
    case "critical":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={{ filter: glowFilter }}
        >
          <polygon
            points="6,11 1,2 11,2"
            fill={fillColor}
            stroke={borderColor}
            strokeWidth={strokeWidth}
          />
        </svg>
      );

    default:
      return null;
  }
};

/**
 * Astro UX Icon Library
 * Based on https://astro-components.netlify.app/?path=/story/components-icons--all-icons
 *
 * Categories: Astro (space/mission), Equipment, System
 */
const ICON_PATHS: Record<string, string> = {
  // Astro Icons (Space/Mission) - Paths from @astrouxds/astro-web-components
  thermal:
    "M10.5 2C12.433 2 14 3.567 14 5.5L14.0005 12.2576C15.2217 13.2664 16 14.7923 16 16.5C16 19.5376 13.5376 22 10.5 22C7.46243 22 5 19.5376 5 16.5C5 14.7919 5.77868 13.2656 7.00044 12.2568L7 5.5C7 3.567 8.567 2 10.5 2ZM10.5 4C9.7203 4 9.07955 4.59489 9.00687 5.35543L9 5.49987L9.0005 13.199L8.27384 13.799C7.47135 14.4616 7 15.4402 7 16.5C7 18.433 8.567 20 10.5 20C12.433 20 14 18.433 14 16.5C14 15.5111 13.5896 14.593 12.8828 13.9362L12.7268 13.7995L12.0006 13.1997L12 5.5C12 4.7203 11.4051 4.07955 10.6445 4.00687L10.5 4ZM11 5.5C11 5.22386 10.7761 5 10.5 5C10.2239 5 10 5.22386 10 5.5L10.0006 14.0499C8.85917 14.2813 8 15.2903 8 16.5C8 17.8807 9.11929 19 10.5 19C11.8807 19 13 17.8807 13 16.5C13 15.2907 12.1413 14.2819 11.0004 14.0501L11 5.5ZM20 11C20.5523 11 21 11.4477 21 12C21 12.5523 20.5523 13 20 13H17C16.4477 13 16 12.5523 16 12C16 11.4477 16.4477 11 17 11H20ZM19 9C19 8.44772 18.5523 8 18 8H17C16.4477 8 16 8.44772 16 9C16 9.55228 16.4477 10 17 10H18C18.5523 10 19 9.55228 19 9ZM20 5C20.5523 5 21 5.44772 21 6C21 6.55228 20.5523 7 20 7H17C16.4477 7 16 6.55228 16 6C16 5.44772 16.4477 5 17 5H20ZM19 3C19 2.44772 18.5523 2 18 2H17C16.4477 2 16 2.44772 16 3C16 3.55228 16.4477 4 17 4H18C18.5523 4 19 3.55228 19 3Z",
  "propulsion-power":
    "M16 2C17.1046 2 18 2.89543 18 4V20C18 21.1046 17.1046 22 16 22H9C7.89543 22 7 21.1046 7 20V4C7 2.89543 7.89543 2 9 2H16ZM13.8115 5.69961C13.8115 5.00797 12.9835 4.75858 12.631 5.27555L12.5794 5.36359L9.0799 12.364C8.8597 12.8083 9.11904 13.3258 9.55954 13.3928L9.6566 13.4001H11.1901V18.3004C11.1901 18.992 12.0181 19.2414 12.3706 18.7245L12.4222 18.6364L15.9217 11.636C16.1419 11.1917 15.8711 10.6742 15.4398 10.6072L15.345 10.5999H13.8115V5.69961Z",
  payload:
    "M12.6802 2.24483L12.8418 2.31637L20.8418 6.31637C21.4305 6.6107 21.819 7.18871 21.8753 7.83667L21.8824 8V16C21.8824 16.6581 21.5391 17.2641 20.9848 17.6043L20.8418 17.6836L12.8418 21.6836C12.3649 21.9221 11.812 21.9459 11.3198 21.7552L11.1582 21.6836L3.15821 17.6836C2.56956 17.3893 2.18104 16.8113 2.12475 16.1633L2.11768 16V8C2.11768 7.34186 2.46091 6.73586 3.0153 6.39574L3.15821 6.31637L3.4276 6.17973C3.50773 6.12386 3.59511 6.0805 3.6866 6.05036L11.1582 2.31637C11.5822 2.1044 12.0662 2.062 12.5141 2.18919L12.6802 2.24483ZM3.99968 8.17865L4.00003 16L10.9997 19.4996V12.5536L3.99968 8.17865ZM12.9997 12.6176L19.9997 9.11765L20 16L12.9997 19.4996V12.6176ZM18.5 15C18.7762 15 19 15.2239 19 15.5C19 15.7761 18.7762 16 18.5 16C18.2239 16 18 15.7761 18 15.5C18 15.2239 18.2239 15 18.5 15ZM6.00003 15.5C6.00003 15.2239 5.77617 15 5.50003 15C5.22389 15 5.00003 15.2239 5.00003 15.5C5.00003 15.7761 5.22389 16 5.50003 16C5.77617 16 6.00003 15.7761 6.00003 15.5ZM16.5 14C16.7762 14 17 14.2239 17 14.5C17 14.7761 16.7762 15 16.5 15C16.2239 15 16 14.7761 16 14.5C16 14.2239 16.2239 14 16.5 14ZM8.00003 14.5C8.00003 14.2239 7.77617 14 7.50003 14C7.22389 14 7.00003 14.2239 7.00003 14.5C7.00003 14.7761 7.22389 15 7.50003 15C7.77617 15 8.00003 14.7761 8.00003 14.5ZM9.50003 13C9.77617 13 10 13.2239 10 13.5C10 13.7761 9.77617 14 9.50003 14C9.22389 14 9.00003 13.7761 9.00003 13.5C9.00003 13.2239 9.22389 13 9.50003 13ZM15 13.5C15 13.2239 14.7762 13 14.5 13C14.2239 13 14 13.2239 14 13.5C14 13.7761 14.2239 14 14.5 14C14.7762 14 15 13.7761 15 13.5ZM12 4L5.93668 7.03065L12.053 10.854L18.8807 7.44065L12 4ZM12.5 9.5C12.5 9.22386 12.2762 9 12 9C11.7239 9 11.5 9.22386 11.5 9.5C11.5 9.77614 11.7239 10 12 10C12.2762 10 12.5 9.77614 12.5 9.5ZM12 7C12.2762 7 12.5 7.22386 12.5 7.5C12.5 7.77614 12.2762 8 12 8C11.7239 8 11.5 7.77614 11.5 7.5C11.5 7.22386 11.7239 7 12 7ZM12.5 5.5C12.5 5.22386 12.2762 5 12 5C11.7239 5 11.5 5.22386 11.5 5.5C11.5 5.77614 11.7239 6 12 6C12.2762 6 12.5 5.77614 12.5 5.5Z",
  netcom:
    "M16 2C17.6569 2 19 3.34315 19 5C19 6.65685 17.6569 8 16 8C15.8259 8 15.6553 7.98518 15.4894 7.95672L14.8034 9.14676C15.3137 9.64825 15.6902 10.2856 15.874 11.0002L18.2676 10.9999C18.6134 10.4022 19.2597 10 20 10C21.1046 10 22 10.8954 22 12C22 13.1046 21.1046 14 20 14C19.2602 14 18.6142 13.5983 18.2682 13.0011L15.8738 13.0008C15.6717 13.7853 15.2375 14.4767 14.6488 14.9974L15.2128 16.1044C15.4637 16.0363 15.7276 16 16 16C17.6569 16 19 17.3431 19 19C19 20.6569 17.6569 22 16 22C14.3431 22 13 20.6569 13 19C13 18.3508 13.2062 17.7498 13.5567 17.2589L12.8669 15.9058C12.5878 15.9675 12.2977 16 12 16C11.2582 16 10.5635 15.7981 9.96801 15.4462L7.75736 17.6569L7.59355 17.4912C7.85196 17.9345 8 18.4499 8 19C8 20.6569 6.65685 22 5 22C3.34315 22 2 20.6569 2 19C2 17.3431 3.34315 16 5 16C5.55006 16 6.06555 16.148 6.50879 16.4065L6.34315 16.2426L8.55383 14.032C8.20193 13.4365 8 12.7418 8 12C8 9.79086 9.79086 8 12 8C12.3705 8 12.7293 8.05038 13.0698 8.14467L13.7446 6.97823C13.281 6.45014 13 5.75789 13 5C13 3.34315 14.3431 2 16 2ZM12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10Z",
  antenna:
    "M12.5 8C13.6506 8 14.5833 8.89543 14.5833 10C14.5833 10.7398 14.1649 11.3858 13.5428 11.7318L13.5416 22H11.4583L11.4582 11.7324C10.8356 11.3866 10.4166 10.7403 10.4166 10C10.4166 8.89543 11.3494 8 12.5 8ZM6.63099 2.32732C6.98124 2.70075 7.0016 3.27819 6.64763 3.65177C3.61283 6.82949 3.34936 11.6553 6.02151 15.1201C6.33286 15.5271 6.26451 16.1001 5.86774 16.4431C5.40872 16.8398 4.68582 16.775 4.3192 16.294C1.13831 12.1519 1.38131 6.45292 4.85709 2.56983L5.07393 2.33438L5.1678 2.24643C5.59314 1.89421 6.24722 1.91815 6.63099 2.32732ZM19.9142 2.24643L20.0081 2.33438C23.6894 6.21966 24.0087 12.0674 20.7628 16.294C20.3962 16.775 19.6733 16.8398 19.2143 16.4431C18.8175 16.1001 18.7492 15.5271 19.0605 15.1201C21.7327 11.6553 21.4692 6.82949 18.4344 3.65177C18.0804 3.27819 18.1008 2.70075 18.4511 2.32732C18.8348 1.91815 19.4889 1.89421 19.9142 2.24643ZM9.8013 5.34518C10.1466 5.71337 10.1537 6.26509 9.82646 6.65682C8.45162 8.33503 8.32337 10.6773 9.50741 12.4838C9.79011 12.9062 9.72281 13.4541 9.33916 13.7858C8.86156 14.1986 8.10517 14.1177 7.75278 13.6059C6.10277 11.1439 6.21558 7.93692 8.0237 5.58971L8.20397 5.3653L8.28854 5.2755C8.70418 4.88648 9.40006 4.91737 9.8013 5.34518ZM16.7935 5.2755L16.8781 5.3653C18.8573 7.72924 19.0325 11.0644 17.3292 13.6059C16.9769 14.1177 16.2205 14.1986 15.7429 13.7858C15.3592 13.4541 15.2919 12.9062 15.5746 12.4838C16.7586 10.6773 16.6304 8.33503 15.2556 6.65682C14.9283 6.26509 14.9354 5.71337 15.2807 5.34518C15.682 4.91737 16.3778 4.88648 16.7935 5.2755Z",
  solar:
    "M19.5385 4C20.3456 4 21 4.67008 21 5.49666V17.5033C21 18.3299 20.3456 19 19.5385 19H3.46154C2.65436 19 2 18.3299 2 17.5033V5.49666C2 4.67008 2.65436 4 3.46154 4H19.5385ZM7 6H4V11H7V6ZM11 6H8V11H11V6ZM15 6H12V11H15V6ZM19 6H16V11H19V6ZM7 12H4V17H7V12ZM11 12H8V17H11V12ZM15 12H12V17H15V12ZM19 12H16V17H19V12Z",
  altitude:
    "M12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2ZM12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4ZM14 18C14 17.4477 13.5523 17 13 17H11C10.4477 17 10 17.4477 10 18C10 18.5523 10.4477 19 11 19H13C13.5523 19 14 18.5523 14 18ZM14 14C14.5523 14 15 14.4477 15 15C15 15.5523 14.5523 16 14 16H10C9.44772 16 9 15.5523 9 15C9 14.4477 9.44772 14 10 14H14ZM16 12L19 14V10L16 12ZM5 10L8 12L5 14V10ZM14 12C14 11.4477 13.5523 11 13 11H11C10.4477 11 10 11.4477 10 12C10 12.5523 10.4477 13 11 13H13C13.5523 13 14 12.5523 14 12ZM14 8C14.5523 8 15 8.44772 15 9C15 9.55228 14.5523 10 14 10H10C9.44772 10 9 9.55228 9 9C9 8.44772 9.44772 8 10 8H14ZM14 6C14 5.44772 13.5523 5 13 5H11C10.4477 5 10 5.44772 10 6C10 6.55228 10.4477 7 11 7H13C13.5523 7 14 6.55228 14 6Z",
  processor:
    "M9 20V22H7V20H9ZM13 20V22H11V20H13ZM17 20V22H15V20H17ZM17 5C18.1046 5 19 5.89543 19 7V17C19 18.1046 18.1046 19 17 19H7C5.89543 19 5 18.1046 5 17V7C5 5.89543 5.89543 5 7 5H17ZM17 16C16.4477 16 16 16.4477 16 17C16 17.5523 16.4477 18 17 18C17.5523 18 18 17.5523 18 17C18 16.4477 17.5523 16 17 16ZM4 15V17H2V15H4ZM22 15V17H20V15H22ZM4 11V13H2V11H4ZM22 11V13H20V11H22ZM4 7V9H2V7H4ZM22 7V9H20V7H22ZM7 6C6.44772 6 6 6.44772 6 7C6 7.55228 6.44772 8 7 8C7.55228 8 8 7.55228 8 7C8 6.44772 7.55228 6 7 6ZM9 2V4H7V2H9ZM13 2V4H11V2H13ZM17 2V4H15V2H17Z",
  mission:
    "M12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2ZM12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4ZM18 12C18 8.68629 15.3137 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18C15.3137 18 18 15.3137 18 12ZM12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8ZM14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14C13.1046 14 14 13.1046 14 12Z",
  equipment:
    "M22 5C22 3.89543 21.1046 3 20 3H4C2.89543 3 2 3.89543 2 5V9C2 10.1046 2.89543 11 4 11H20C21.1046 11 22 10.1046 22 9V5ZM22 15C22 13.8954 21.1046 13 20 13H4C2.89543 13 2 13.8954 2 15V19C2 20.1046 2.89543 21 4 21H20C21.1046 21 22 20.1046 22 19V15ZM4 15H20V19H4V15ZM7 16V18H5V16H7ZM10 18V16H8V18H10ZM13 16V18H11V16H13ZM20 5H4V9H20V5ZM7 8V6H5V8H7ZM10 6V8H8V6H10ZM13 8V6H11V8H13Z",
  "satellite-transmit":
    "M8.87871 8.87868C9.65976 8.09763 10.9261 8.09763 11.7071 8.87868L13.1213 10.2929C13.9024 11.0739 13.9024 12.3403 13.1213 13.1213C12.3403 13.9024 11.074 13.9024 10.2929 13.1213L8.87871 11.7071C8.09766 10.9261 8.09766 9.65973 8.87871 8.87868ZM4.05657 15.3702C4.09551 15.3313 4.13761 15.2956 4.18243 15.2636L8.1716 12.4142L9.58581 13.8284L6.73641 17.8176C6.4154 18.267 5.79085 18.3711 5.34144 18.0501C5.29662 18.0181 5.25452 17.9824 5.21557 17.9435L4.05657 16.7845C3.66604 16.3939 3.66604 15.7608 4.05657 15.3702ZM20.9898 11C21.5828 11 22.0551 11.5125 21.9948 12.1055C21.4823 17.3315 17.3416 21.4721 12.1156 21.9947C11.5226 22.055 11 21.5927 11 20.9998C11 20.4973 11.3719 20.0551 11.8744 20.0048C16.1658 19.5928 19.5828 16.1757 19.9949 11.8844C20.0451 11.3819 20.4773 11 20.9898 11ZM16.9776 11C17.5946 11 18.08 11.546 17.989 12.1527C17.5035 15.1355 15.1469 17.5015 12.1531 17.9868C11.5462 18.088 11 17.5925 11 16.9757C11 16.4803 11.3641 16.0657 11.8597 15.9848C13.8884 15.6338 15.5032 14.0794 15.9425 12.083L15.9864 11.8595C16.0673 11.364 16.482 11 16.9776 11ZM16.6903 3.97335L16.7845 4.05653L17.9435 5.21554C17.9824 5.25449 18.0181 5.29659 18.0501 5.34141C18.3464 5.75625 18.2805 6.32032 17.9152 6.65713L17.8176 6.73638L13.8285 9.58578L12.4142 8.17157L15.2636 4.1824C15.2957 4.13758 15.3313 4.09548 15.3703 4.05653C15.7308 3.69605 16.298 3.66832 16.6903 3.97335Z",
  "satellite-receive":
    "M5.53115 17.8387L6.58945 18.8268L8.92522 18.602L9.83101 20.4262C9.89964 20.5644 9.93535 20.7167 9.93535 20.871C9.93535 21.3838 9.54931 21.8065 9.05197 21.8642L8.93535 21.871H4.20497C3.9626 21.871 3.72849 21.7829 3.54616 21.6233C3.16263 21.2874 3.09746 20.7233 3.37588 20.3117L3.45266 20.2122L5.53115 17.8387ZM7.09757 10.0443C7.33377 10.0636 7.55543 10.1662 7.72301 10.3338L10.042 12.653L12 12L11.347 13.958L13.2146 15.8254C13.6051 16.2159 13.6051 16.8491 13.2146 17.2396C13.047 17.4072 12.8253 17.5098 12.5891 17.5291L7.30432 17.9622C7.01163 17.9862 6.7232 17.8803 6.51554 17.6726L5.87575 17.0328C5.66809 16.8252 5.56221 16.5368 5.58619 16.2441L6.01924 10.9593C6.06434 10.4088 6.54713 9.99916 7.09757 10.0443ZM14 9C14.5523 9 15 9.44772 15 10C15 10.5523 14.5523 11 14 11C13.4477 11 13 10.5523 13 10C13 9.44772 13.4477 9 14 9ZM16 7C16.5523 7 17 7.44772 17 8C17 8.55228 16.5523 9 16 9C15.4477 9 15 8.55228 15 8C15 7.44772 15.4477 7 16 7ZM18.5 4C19.3284 4 20 4.67157 20 5.5C20 6.32843 19.3284 7 18.5 7C17.6716 7 17 6.32843 17 5.5C17 4.67157 17.6716 4 18.5 4Z",
  "satellite-off":
    "M8.87867 11.7071C8.09762 10.9261 8.09762 9.65973 8.87867 8.87868C9.65972 8.09763 10.926 8.09763 11.7071 8.87868L13.1213 10.2929C13.9024 11.0739 13.9024 12.3403 13.1213 13.1213C12.3403 13.9024 11.0739 13.9024 10.2929 13.1213L8.87867 11.7071ZM4.05653 16.7845C3.61958 16.3475 3.67956 15.6228 4.1824 15.2636L8.17156 12.4142L9.58578 13.8284L6.73637 17.8176C6.3772 18.3204 5.65248 18.3804 5.21553 17.9435L4.05653 16.7845ZM15.2636 4.1824C15.6228 3.67956 16.3475 3.61958 16.7844 4.05653L17.9435 5.21554C18.3804 5.65249 18.3204 6.37721 17.8176 6.73638L13.8284 9.58578L12.4142 8.17157L15.2636 4.1824Z",
  hardware:
    "M4 17.8247V10.4692H20V17.8247H4ZM2 9.77297C2 9.05293 2.58371 8.46922 3.30375 8.46922H20.6962C21.4163 8.46922 22 9.05293 22 9.77298V18.5209C22 19.241 21.4163 19.8247 20.6962 19.8247H3.30375C2.58371 19.8247 2 19.241 2 18.5209V9.77297ZM8.99573 13.637H4.99573V11.637H8.99573V13.637ZM8.99573 16.642H4.99573V14.642H8.99573V16.642ZM10 13.637H14V11.637H10V13.637ZM14 16.642H10V14.642H14V16.642ZM14.9987 13.637H18.9987V11.637H14.9987V13.637ZM18.9987 16.6424H14.9987V14.6424H18.9987V16.6424Z",
  release: "M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z",
  // Legacy aliases
  satellite:
    "M8.87867 11.7071C8.09762 10.9261 8.09762 9.65973 8.87867 8.87868C9.65972 8.09763 10.926 8.09763 11.7071 8.87868L13.1213 10.2929C13.9024 11.0739 13.9024 12.3403 13.1213 13.1213C12.3403 13.9024 11.0739 13.9024 10.2929 13.1213L8.87867 11.7071ZM4.05653 16.7845C3.61958 16.3475 3.67956 15.6228 4.1824 15.2636L8.17156 12.4142L9.58578 13.8284L6.73637 17.8176C6.3772 18.3204 5.65248 18.3804 5.21553 17.9435L4.05653 16.7845ZM15.2636 4.1824C15.6228 3.67956 16.3475 3.61958 16.7844 4.05653L17.9435 5.21554C18.3804 5.65249 18.3204 6.37721 17.8176 6.73638L13.8284 9.58578L12.4142 8.17157L15.2636 4.1824Z",
  power:
    "M16 2C17.1046 2 18 2.89543 18 4V20C18 21.1046 17.1046 22 16 22H9C7.89543 22 7 21.1046 7 20V4C7 2.89543 7.89543 2 9 2H16ZM13.8115 5.69961C13.8115 5.00797 12.9835 4.75858 12.631 5.27555L12.5794 5.36359L9.0799 12.364C8.8597 12.8083 9.11904 13.3258 9.55954 13.3928L9.6566 13.4001H11.1901V18.3004C11.1901 18.992 12.0181 19.2414 12.3706 18.7245L12.4222 18.6364L15.9217 11.636C16.1419 11.1917 15.8711 10.6742 15.4398 10.6072L15.345 10.5999H13.8115V5.69961Z",
  comms:
    "M16 2C17.6569 2 19 3.34315 19 5C19 6.65685 17.6569 8 16 8C15.8259 8 15.6553 7.98518 15.4894 7.95672L14.8034 9.14676C15.3137 9.64825 15.6902 10.2856 15.874 11.0002L18.2676 10.9999C18.6134 10.4022 19.2597 10 20 10C21.1046 10 22 10.8954 22 12C22 13.1046 21.1046 14 20 14C19.2602 14 18.6142 13.5983 18.2682 13.0011L15.8738 13.0008C15.6717 13.7853 15.2375 14.4767 14.6488 14.9974L15.2128 16.1044C15.4637 16.0363 15.7276 16 16 16C17.6569 16 19 17.3431 19 19C19 20.6569 17.6569 22 16 22C14.3431 22 13 20.6569 13 19C13 18.3508 13.2062 17.7498 13.5567 17.2589L12.8669 15.9058C12.5878 15.9675 12.2977 16 12 16C11.2582 16 10.5635 15.7981 9.96801 15.4462L7.75736 17.6569L7.59355 17.4912C7.85196 17.9345 8 18.4499 8 19C8 20.6569 6.65685 22 5 22C3.34315 22 2 20.6569 2 19C2 17.3431 3.34315 16 5 16C5.55006 16 6.06555 16.148 6.50879 16.4065L6.34315 16.2426L8.55383 14.032C8.20193 13.4365 8 12.7418 8 12C8 9.79086 9.79086 8 12 8C12.3705 8 12.7293 8.05038 13.0698 8.14467L13.7446 6.97823C13.281 6.45014 13 5.75789 13 5C13 3.34315 14.3431 2 16 2ZM12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10Z",
};

/**
 * Render percentage arc display (Astro UX pattern)
 *
 * Displays a circular arc showing a percentage value 0-100.
 * The arc fills clockwise from the top, with status-colored fill.
 */
const renderPercentageArc = (
  percentage: number,
  statusColor: string,
  showValue: boolean = true,
  size: number = 48,
): React.ReactNode => {
  // Clamp percentage to 0-100
  const pct = Math.max(0, Math.min(100, percentage));

  // Arc geometry
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = size / 2 - 4; // Leave room for stroke
  const strokeWidth = 4;
  const innerRadius = radius - strokeWidth / 2;

  // Calculate arc path
  // Start at top (12 o'clock) and sweep clockwise
  const startAngle = -90; // Start at top
  const endAngle = startAngle + (pct / 100) * 360;

  // Convert to radians
  const startRad = (startAngle * Math.PI) / 180;
  const endRad = (endAngle * Math.PI) / 180;

  // Calculate start and end points
  const startX = centerX + innerRadius * Math.cos(startRad);
  const startY = centerY + innerRadius * Math.sin(startRad);
  const endX = centerX + innerRadius * Math.cos(endRad);
  const endY = centerY + innerRadius * Math.sin(endRad);

  // Determine if we need a large arc (> 180 degrees)
  const largeArcFlag = pct > 50 ? 1 : 0;

  // Create arc path
  const arcPath =
    pct > 0 && pct < 100
      ? `M ${startX} ${startY} A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 1 ${endX} ${endY}`
      : pct >= 100
        ? `M ${centerX} ${centerY - innerRadius} A ${innerRadius} ${innerRadius} 0 1 1 ${centerX - 0.001} ${centerY - innerRadius}` // Full circle
        : ""; // Empty when 0%

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ display: "block" }}
    >
      {/* Background circle (track) */}
      <circle
        cx={centerX}
        cy={centerY}
        r={innerRadius}
        fill="none"
        stroke="rgba(255, 255, 255, 0.1)"
        strokeWidth={strokeWidth}
      />

      {/* Progress arc */}
      {arcPath && (
        <path
          d={arcPath}
          fill="none"
          stroke={statusColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          style={{
            filter: `drop-shadow(0 0 4px ${statusColor}60)`,
          }}
        />
      )}

      {/* Center value text */}
      {showValue && (
        <text
          x={centerX}
          y={centerY}
          textAnchor="middle"
          dominantBaseline="central"
          fill={statusColor}
          fontSize={size * 0.28}
          fontWeight={600}
          fontFamily="system-ui, -apple-system, sans-serif"
        >
          {Math.round(pct)}
        </text>
      )}
    </svg>
  );
};

/**
 * Get status based on percentage value
 * - ascending: low values = critical, high values = normal
 * - descending: low values = normal, high values = critical
 */
const getStatusFromPercentage = (
  percentage: number,
  direction: "ascending" | "descending",
): MonitoringStatus => {
  const pct = direction === "descending" ? 100 - percentage : percentage;

  if (pct >= 80) return "normal";
  if (pct >= 60) return "standby";
  if (pct >= 40) return "caution";
  if (pct >= 20) return "serious";
  return "critical";
};

/**
 * MonitoringIcon - Pure React monitoring icon component
 *
 * Displays system status with icon, label, and optional notification badge.
 * Fully Astro UX compliant with theme integration.
 *
 * @example
 * ```tsx
 * // Standard monitoring icon
 * <MonitoringIcon
 *   status="normal"
 *   label="Power"
 *   sublabel="85%"
 *   icon="power"
 * />
 *
 * // Percentage arc display
 * <MonitoringIcon
 *   status="normal"
 *   label="Battery"
 *   showPercentage
 *   percentage={75}
 *   percentageToStatus="ascending"
 * />
 * ```
 */
export const MonitoringIcon = memo(function MonitoringIcon({
  status,
  label,
  sublabel,
  icon = "mission",
  notifications,
  tooltip,
  className = "",
  onClick,
  showPercentage = false,
  percentage = 0,
  percentageToStatus,
  showPercentageValue = true,
}: MonitoringIconProps): React.ReactElement {
  const { tokens, mode, theme } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  const [showTooltip, setShowTooltip] = useState(false);

  // Calculate effective status when using percentage-to-status mapping
  const effectiveStatus = percentageToStatus
    ? getStatusFromPercentage(percentage, percentageToStatus)
    : status;

  // Get status color from theme
  const color = tokens.colors.status[effectiveStatus] || "#a4abb6";
  const isLightTheme = mode === "light";
  const borderColor = isLightTheme
    ? STATUS_BORDER_COLORS[effectiveStatus]
    : undefined;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onClick();
    }
  };

  const renderIcon = () => {
    // Percentage arc display mode
    if (showPercentage) {
      return renderPercentageArc(percentage, color, showPercentageValue, 48);
    }

    // Custom React element
    if (React.isValidElement(icon)) {
      return icon;
    }

    // Use Icon component if valid icon name from core library
    if (typeof icon === "string" && isValidIconName(icon)) {
      return <Icon name={icon as IconName} size={24} color="currentColor" />;
    }

    // Fallback to legacy ICON_PATHS for backward compatibility
    const iconPath = typeof icon === "string" ? ICON_PATHS[icon] : undefined;

    return (
      <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        style={{ width: 24, height: 24 }}
        aria-hidden="true"
      >
        {iconPath ? (
          <path d={iconPath} fillRule="evenodd" clipRule="evenodd" />
        ) : (
          <path
            d="M12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2ZM12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4ZM18 12C18 8.68629 15.3137 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18C15.3137 18 18 15.3137 18 12ZM12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8ZM14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14C13.1046 14 14 13.1046 14 12Z"
            fillRule="evenodd"
            clipRule="evenodd"
          />
        )}
      </svg>
    );
  };

  return (
    <div
      className={classNames("zendir-monitoring-icon", className)}
      onClick={onClick}
      onKeyDown={onClick ? handleKeyDown : undefined}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      tabIndex={onClick ? 0 : undefined}
      role="status"
      aria-label={`${label}: ${sublabel ?? status}`}
      style={{
        position: "relative",
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        gap: tokens.spacing.xs,
        padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
        minWidth: 72,
        backgroundColor: isTransparentTheme
          ? "transparent"
          : tokens.colors.background.surface,
        ...(isTransparentTheme && {
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)" as const,
        }),
        ...(tokens.colors.border.cardStyle ?? {
          border: `1px solid ${tokens.colors.border.muted}`,
        }),
        borderRadius: tokens.borderRadius.md,
        cursor: onClick ? "pointer" : "default",
        outline: "none",
        transition: tokens.animation.fast,
      }}
    >
      {/* Icon with status indicator */}
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color,
          // Explicit sizing for predictable layout
          width: showPercentage ? 48 : 24,
          height: showPercentage ? 48 : 24,
        }}
      >
        {renderIcon()}

        {/* Status symbol - shape based on status per Astro UX */}
        <span
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            transform: "translate(-35%, -35%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {renderStatusSymbol(
            effectiveStatus,
            9,
            color,
            borderColor,
            !isLightTheme,
          )}
        </span>

        {/* Notification badge */}
        {notifications !== undefined && notifications > 0 && (
          <span
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              transform: "translate(40%, -40%)",
              minWidth: 16,
              height: 16,
              padding: "0 4px",
              borderRadius: tokens.borderRadius.full,
              backgroundColor: tokens.colors.status.critical,
              color: "#ffffff",
              fontSize: "0.625rem", // 10px in rem
              fontWeight: 700, // AstroUXDS bold for status values
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {notifications > 99 ? "99+" : notifications}
          </span>
        )}
      </div>

      {/* Label - sentence case per Astro UX */}
      <span
        style={{
          fontSize: tokens.typography.fontSize.xs,
          fontWeight: tokens.typography.fontWeight.medium,
          color: tokens.colors.text.secondary,
          textTransform: "none", // Astro uses sentence case, not uppercase
          letterSpacing: tokens.typography.letterSpacing.normal,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>

      {/* Sublabel - dynamic value */}
      {sublabel && (
        <span
          style={{
            fontSize: tokens.typography.fontSize.sm,
            fontWeight: tokens.typography.fontWeight.semibold,
            fontVariantNumeric: "tabular-nums",
            color: tokens.colors.text.primary,
          }}
        >
          {sublabel}
        </span>
      )}

      {/* Tooltip */}
      {tooltip && showTooltip && (
        <div
          style={{
            position: "absolute",
            bottom: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            marginBottom: 8,
            padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
            backgroundColor: tokens.colors.background.overlay,
            borderRadius: tokens.borderRadius.sm,
            fontSize: tokens.typography.fontSize.sm,
            color: tokens.colors.text.primary,
            whiteSpace: "nowrap",
            zIndex: 1000,
            pointerEvents: "none",
            boxShadow: tokens.shadows.md,
          }}
        >
          {tooltip}
          {/* Tooltip arrow */}
          <span
            style={{
              position: "absolute",
              top: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              borderWidth: 4,
              borderStyle: "solid",
              borderColor: `${tokens.colors.background.overlay} transparent transparent transparent`,
            }}
          />
        </div>
      )}
    </div>
  );
});
